import React from 'react';
import { useSelector } from 'react-redux'
import { HashLink as Link } from 'react-router-hash-link';
import { GlobalStore } from '../../../store';
import User from '../../../models/User';
import { Lock, Star } from '../../../viewcomponents/FontAwesome';

export default (): JSX.Element | null => {

  const user = useSelector<GlobalStore, User | null>(store => store.user);

  return (
    user
      ? <div className="card p-3 mb-3">
          <h2 id="user">Users</h2>

          <h3 id="join">Join a queue</h3>
          <p>
            First find the queue you want (see <Link smooth to="/help#findAQueue">Find a queue</Link>) and
            click its name in the list — for example <em>Inda</em>:
          </p>
          <div className="mb-3" style={{maxWidth: '26rem'}}>
            <div className="card p-2" style={{cursor: 'pointer', fontSize: '1.3rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
              <span className="theme-text" style={{fontWeight: 500}}>Inda</span>
              <span style={{color: '#6c757d', fontSize: '0.9rem'}}>7 in queue</span>
            </div>
          </div>
          <p>
            On the queue page, fill in your <strong>location</strong> and a <strong>comment</strong> describing
            what you need. On a KTH computer your location is filled in automatically; otherwise type it in.
            Both fields are required — the <em>Join queue</em> button stays grey and unclickable until you have
            entered both:
          </p>
          <div className="mb-2" style={{maxWidth: '26rem'}}>
            <div className="text-center text-white" style={{lineHeight: '3em', background: '#A9A9A9'}}>
              <strong>Join queue</strong>
            </div>
            <small className="text-muted">Comment empty — not clickable</small>
          </div>
          <div className="mb-3" style={{maxWidth: '26rem'}}>
            <div className="text-center text-white theme" style={{lineHeight: '3em'}}>
              <strong>Join queue</strong>
            </div>
            <small className="text-muted">Location and comment filled — click to join</small>
          </div>

          <h3 id="leave">Leave a queue</h3>
          <p>
            To leave a queue, you must first join a queue.
            If you haven't joined the queue you want to leave,
            we recommend you to not join that queue.
            If you have already joined the queue you want to leave,
            go to the main page of the queue and click the <em>Leave queue</em> button.
          </p>

          <h3 id="bookedSlots">Booked lab slots</h3>
          <div className="row">
            <div className="col-lg-7">
              <p>
                If your teacher has set up bookings, you can reserve a lab slot in advance. Your booking shows
                as a badge under the <em>Join queue</em> form. It stays visible all day and turns orange from
                30 minutes before until 30 minutes after your slot, so you know when it is active.
              </p>
              <ul>
                <li>
                  <strong>Priority near your slot.</strong> A few minutes before the slot time, an active
                  booking pushes you toward the top of the queue — ahead of students without a booking.
                </li>
                <li>
                  <strong>Queue for what you booked.</strong> The booking only counts if you join the queue
                  for the same type you reserved. Book <em>Present</em> and you must queue as <em>Present</em>;
                  queue as <em>Help</em> instead and the badge stays grey with “no booking priority”.
                </li>
                <li>
                  <strong>The teacher uploads the bookings.</strong> If the teacher hasn't uploaded bookings —
                  or forgets to — no badge appears and the queue simply works first-come, first-served. Check
                  with your teacher if you expected a slot and don't see one.
                </li>
              </ul>
            </div>
            <div className="col-lg-5">
              <div style={{position: 'relative', marginLeft: '0.5rem', paddingLeft: '22px', borderLeft: '2px solid #ddd'}}>
                <div style={{position: 'relative', marginBottom: '18px'}}>
                  <span style={{position: 'absolute', left: '-29px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#bbb'}} />
                  <div style={{fontWeight: 700, fontSize: '0.9rem'}}>08:00 — earlier that day</div>
                  <div className="text-muted" style={{fontSize: '0.8rem', margin: '0.1rem 0 0.4rem'}}>
                    Listed under <em>Join queue</em>, but greyed out — the slot is still hours away.
                  </div>
                  <span style={{display: 'inline-block', textAlign: 'center', padding: '4px 10px', borderRadius: '4px', background: '#e0e0e0', color: '#888'}}>
                    <strong>Present</strong> booked at 11:45
                  </span>
                </div>
                <div style={{position: 'relative'}}>
                  <span style={{position: 'absolute', left: '-29px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#e67e22'}} />
                  <div style={{fontWeight: 700, fontSize: '0.9rem'}}>11:15 — 30 min before</div>
                  <div className="text-muted" style={{fontSize: '0.8rem', margin: '0.1rem 0 0.4rem'}}>
                    The badge lights up — join the queue as <em>Present</em> now to claim your slot.
                  </div>
                  <span style={{display: 'inline-block', textAlign: 'center', padding: '4px 10px', borderRadius: '4px', background: '#e67e22', color: '#fff'}}>
                    <strong>Present</strong> booked at 11:45
                  </span>
                </div>
              </div>
              <small className="text-muted d-block mt-2">
                Shown under the <em>Join queue</em> form on the queue page.
              </small>
            </div>
          </div>

          <h3 id="receivingHelp">Receiving help</h3>
          <p>
            When an assistant starts helping you, you are marked as <em>receiving help</em> so that other
            assistants know not to come over. The assistant can do this from their phone — but if they
            haven't, you can do it yourself.
          </p>
          <p>
            Once you have <Link smooth to="/help#join">joined</Link> a queue, a green
            <em> Receiving help</em> button appears. Click it and a <em>Who is helping you?</em> box pops
            up listing the assistants currently on duty — pick the one helping you. You are then shown as
            being helped until the assistant marks you as done.
          </p>

          <h3 id="colors">Colors</h3>
          <p>
            When a person is standing in the queue, they may have different colored backgrounds, here are their respective description.
          </p>
          <h4 id="goldenStar" style={{color: '#EEB868'}}>Golden <Star color="#EEB868" /></h4>
          <p>
            If a person has a golden star next to their name, that means that person is you.
          </p>
          <h4 id="green" className="text-success">Green</h4>
          <p>
            When a person has a green background color, that means there is currently an assistant at the person, whom is either helping or grading them.
          </p>
          <h4 id="red" style={{'color': '#E54932'}}>Red</h4>
          <p>
            When a person has a red background color, that means an assistant has notified the person that they have to
            update their location since the assistant can't find them.
          </p>

          <h3>Queue status</h3>
          <p>
            The queues in Stay A While can have different statuses, which can be good to know.
            The statuses are indicated by the color and style of the queue name.
          </p>
          <table className="table table-striped">
            <tbody><tr>
              <th>Style</th>
              <th>Icon</th>
              <th>Description</th>
            </tr>
            <tr>
              <td><strong>Active queue</strong></td>
              <td></td>
              <td>The queue is active and can be joined</td>
            </tr>
            <tr>
              <td style={{color: 'red'}}><strong>Locked queue</strong></td>
              <td><Lock color="red"/></td>
              <td>The queue is locked and can't be joined</td>
            </tr>
          </tbody></table>

          <h3 id="findAQueue">Find a queue</h3>
          <p>
            The first step to queueing would be to find the queue you wish to join, right? So how is that done?
            Well, first go to the <Link smooth to="/list">Queues</Link> page.
            There you can either scroll to find the queue
            you are searching for, or you could search for the queue.
            To scroll, just scroll - more info on
            scrolling will not be covered in this help section.

            To search for a queue, type the queue name (or parts of the queue name) in the search box at the top of the page.
          </p>
          <p>
            At the top of the <Link smooth to="/list">Queues</Link> page you will also find a <em>Recently visited</em>
            list showing the last 5 queues you opened, so you can jump straight back without searching. This list is
            kept in your browser, so it is specific to the device and browser you are using — it won't follow you to
            another computer, and it resets if you clear your browsing data.
          </p>

          <h3 id="banner">Announcements</h3>
          <p>
            Once in a while an administrator needs to inform users about current or upcoming issues and changes.
            When that happens, an alert box with the message appears at the bottom of the screen. Read it and dismiss it once you're done.
          </p>

          <h3 id="messages">Messages</h3>
          <p>
            A teaching assistant can send a message to you directly, or broadcast one to everyone in the queue.
            When you receive a message it pops up in a box centered on the screen, showing the message and who sent it.
            Close it once you've read it.
          </p>
        </div>
      : null
  );
};
